import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Alert,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import api from "../services/api";

// Helper function to format duration from minutes to a readable string (e.g., 90 -> "1h 30m")
const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) {
    return "0m";
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  let result = "";
  if (hours > 0) {
    result += `${hours}h`;
  }
  if (remainingMinutes > 0) {
    result += ` ${remainingMinutes}m`;
  }
  return result.trim();
};

const TSProjectTaskStatus = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [projectSearch, setProjectSearch] = useState("");
  const [tasks, setTasks] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch available projects (segments) on mount
  useEffect(() => {
    const fetchProjects = async () => {
      // ... same as before
      const token = localStorage.getItem("Token");
      if (!token) {
        setError("No token found in localStorage");
        return;
      }
      try {
        const response = await api.get("api/segments/unique_segments_with_job/", {
          headers: {
            Authorization: `Token ${token}`,
          },
        });

        if (
          response.status === 200 &&
          response.data?.unique_segment_names &&
          Array.isArray(response.data.unique_segment_names)
        ) {
          setProjects(response.data.unique_segment_names);
          if (response.data.unique_segment_names.length > 0) {
            setSelectedProject(response.data.unique_segment_names[0]);
          }
        } else {
          setProjects([]);
          setError("Failed to fetch projects");
        }
      } catch (err) {
        console.error("Failed to fetch projects:", err);
        setError(err.response?.data?.error || "Failed to fetch projects");
      }
    };
    fetchProjects();
  }, []);

  // Fetch tasks whenever the selectedProject changes
  useEffect(() => {
    const fetchTasks = async () => {
      const token = localStorage.getItem("Token");
      if (!selectedProject) return;
      try {
        setLoading(true);
        setTasks({});
        setError(null);
        const response = await api.get(
          `/timesheet/api/tasks/?project=${encodeURIComponent(selectedProject)}`,
          {
            headers: {
              Authorization: `Token ${token}`,
            },
          }
        );
        // The new response is: { ram: [ { task: "...", total_duration: 450 } ] }
        setTasks(response.data || {});
      } catch (err) {
        console.error("Failed to fetch tasks:", err);
        setError(err.response?.data?.error || "Failed to fetch tasks.");
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [selectedProject]);

  const handleProjectChange = (event) => {
    setSelectedProject(event.target.value);
  };

  // UPDATED: Copy table as HTML with the new structure
  const copyTableAsHtml = () => {
    if (!tasks || Object.keys(tasks).length === 0) return;

    let html = `
      <table style="border-collapse: collapse; width: 100%;">
        <thead>
          <tr>
            <th style="border: 1px solid #000; padding: 8px;">User</th>
            <th style="border: 1px solid #000; padding: 8px;">Task</th>
            <th style="border: 1px solid #000; padding: 8px;">Total Duration</th>
          </tr>
        </thead>
        <tbody>
    `;

    Object.keys(tasks).forEach((username) => {
      tasks[username].forEach((task, index) => {
        const durationText = formatDuration(task.total_duration);
        if (index === 0) {
          html += `
            <tr>
              <td style="border: 1px solid #000; padding: 8px;" rowspan="${tasks[username].length}">${username}</td>
              <td style="border: 1px solid #000; padding: 8px;">${task.task}</td>
              <td style="border: 1px solid #000; padding: 8px; text-align: right;">${durationText}</td>
            </tr>
          `;
        } else {
          html += `
            <tr>
              <td style="border: 1px solid #000; padding: 8px;">${task.task}</td>
              <td style="border: 1px solid #000; padding: 8px; text-align: right;">${durationText}</td>
            </tr>
          `;
        }
      });
    });

    html += `</tbody></table>`;

    const type = "text/html";
    const blob = new Blob([html], { type });
    const clipboardItem = new ClipboardItem({ [type]: blob });
    navigator.clipboard.write([clipboardItem]).then(
      () => alert("Tasks table copied to clipboard!"),
      (err) => console.error("Error copying table as HTML: ", err)
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
        <Typography variant="body1" style={{ marginLeft: "10px" }}>
          Loading tasks...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const hasTasks = tasks && Object.keys(tasks).length > 0;
  const filteredProjects = projects.filter((project) =>
    project.toLowerCase().includes(projectSearch.toLowerCase())
  );

  return (
    <Paper elevation={3} style={{ padding: "24px", maxWidth: "800px", margin: "20px auto" }}>
      <Typography variant="h4" gutterBottom>
        Project Task Summary
      </Typography>

      {/* Search and Project Selection - No change */}
      <TextField
        label="Search Projects"
        variant="outlined"
        value={projectSearch}
        onChange={(e) => setProjectSearch(e.target.value)}
        fullWidth
        style={{ marginBottom: "20px" }}
      />
      <FormControl fullWidth variant="outlined" style={{ marginBottom: "20px" }}>
        <InputLabel>Project</InputLabel>
        <Select value={selectedProject} onChange={handleProjectChange} label="Project">
          {filteredProjects.map((project, index) => (
            <MenuItem key={index} value={project}>
              {project}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <IconButton
        onClick={copyTableAsHtml}
        color="primary"
        aria-label="copy table"
        style={{ marginBottom: "10px" }}
        disabled={!hasTasks} // Disable button if no tasks
      >
        <ContentCopy />
      </IconButton>
      
      {/* UPDATED: Simplified Table Rendering */}
      {!hasTasks ? (
        <Alert severity="info">No tasks available for this project.</Alert>
      ) : (
        <>
        {/* Total Hours Summary */}
        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
          {Object.entries(tasks).map(([username, userTasks]) => {
            const userTotal = userTasks.reduce((sum, t) => sum + (t.total_duration || 0), 0);
            return (
              <Paper key={username} variant="outlined" sx={{ px: 2, py: 1, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">{username}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{formatDuration(userTotal)}</Typography>
              </Paper>
            );
          })}
          <Paper variant="outlined" sx={{ px: 2, py: 1, textAlign: 'center', bgcolor: '#e3f2fd' }}>
            <Typography variant="body2" color="text.secondary">Total</Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {formatDuration(Object.values(tasks).flat().reduce((sum, t) => sum + (t.total_duration || 0), 0))}
            </Typography>
          </Paper>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Task</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Total Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(tasks).map(([username, userTasks]) =>
                userTasks.map((task, index) => (
                  <TableRow key={`${username}-${task.task}`}>
                    {index === 0 && (
                      <TableCell rowSpan={userTasks.length} sx={{ verticalAlign: 'top' }}>
                        {username}
                      </TableCell>
                    )}
                    <TableCell>{task.task}</TableCell>
                    <TableCell align="right">
                      {formatDuration(task.total_duration)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}
    </Paper>
  );
};

export default TSProjectTaskStatus;